// DashboardProductos.tsx - Gestión de productos con diseño El Piñón
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Stack,
  Pagination,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Search as SearchIcon,
  CheckCircle,
  Edit,
  Delete,
  Visibility,
  Close as CloseIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore,
} from '@mui/icons-material';
import { productoService, Producto } from '../../../services/productos';
import { categoriasService, CategoriaResponse } from '../../../services/categorias';

// Styled Components con diseño El Piñón
const DashboardContainer = styled(Box)({
  background: 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)',
  minHeight: '100vh',
  padding: '24px',
});

const HeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '32px',
  padding: '24px',
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  borderRadius: '20px',
  color: 'white',
  boxShadow: '0 8px 32px rgba(46, 90, 51, 0.3)',
});

const StatsCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '2px solid #e8f5e8',
  boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(46, 90, 51, 0.2)',
    borderColor: '#2e5a33',
  },
});

const ProductCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '1px solid #e8f5e8',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 25px rgba(46, 90, 51, 0.15)',
    borderColor: '#2e5a33',
  },
});

const ActionButton = styled(Button)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 90, 51, 0.4)',
  },
});

const SearchCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '1px solid #e8f5e8',
  marginBottom: '24px',
});

const StyledTableContainer = styled(Box)({
  borderRadius: '16px',
  border: '1px solid #e8f5e8',
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)',
});

// Styled Accordion para móvil
const StyledAccordion = styled(Accordion)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  border: '1px solid #e8f5e8',
  borderRadius: '12px !important',
  marginBottom: '12px',
  boxShadow: '0 2px 10px rgba(46, 90, 51, 0.08)',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 12px 0',
    borderColor: '#2e5a33',
    boxShadow: '0 4px 20px rgba(46, 90, 51, 0.15)',
  },
});

const AccordionHeader = styled(AccordionSummary)({
  minHeight: '72px',
  padding: '8px 16px',
  '&.Mui-expanded': {
    minHeight: '72px',
    backgroundColor: 'rgba(46, 90, 51, 0.03)',
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    '&.Mui-expanded': {
      margin: '12px 0',
    },
  },
});

const AccordionBody = styled(AccordionDetails)({
  padding: '0 16px 16px 16px',
  backgroundColor: 'rgba(248, 255, 254, 0.5)',
});

const DetailRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '10px 0',
  borderBottom: '1px solid rgba(46, 90, 51, 0.08)',
  '&:last-child': {
    borderBottom: 'none',
  },
});

// Función simple para formatear moneda
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
  }).format(amount);
};

const DashboardProductosBasic: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [filteredProductos, setFilteredProductos] = useState<Producto[]>([]);
  const [categorias, setCategorias] = useState<CategoriaResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Producto | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    categoria: '',
    precio_venta: '',
    stock_actual: '',
    codigo: '',
    imagen: '',
    activo: true,
  });

  useEffect(() => {
    loadProductos();
    loadCategorias();
  }, []);

  useEffect(() => {
    // Aplicar filtro de búsqueda
    if (!Array.isArray(productos)) {
      setFilteredProductos([]);
      return;
    }
    
    if (searchTerm) {
      const filtered = productos.filter(producto =>
        producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        producto.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        producto.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProductos(filtered);
      setCurrentPage(1); // Resetear a página 1 al buscar
    } else {
      setFilteredProductos(productos);
    }
  }, [productos, searchTerm]);

  // Calcular productos paginados
  const totalPages = Math.ceil(filteredProductos.length / ITEMS_PER_PAGE);
  const paginatedProductos = filteredProductos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadProductos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productoService.getProductos();
      setProductos(data);
      console.log('✅ Productos cargados desde Django:', data.length);
    } catch (error) {
      console.error('❌ Error al cargar productos:', error);
      setError('Error al cargar productos desde el backend.');
      setProductos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategorias = async () => {
    try {
      const data = await categoriasService.getAll(); // Cargar todas las categorías
      setCategorias(data);
      console.log('✅ Categorías cargadas desde Django:', data.length);
    } catch (error) {
      console.error('❌ Error al cargar categorías:', error);
      setCategorias([]); // Asegurar que sea un array vacío en caso de error
    }
  };

  const getCategoriaName = (id_categoria: number): string => {
    if (!Array.isArray(categorias)) return 'Sin categoría';
    const categoria = categorias.find(cat => cat.id === id_categoria);
    return categoria?.nombre || 'Sin categoría';
  };

  const handleOpenDialog = (producto?: Producto) => {
    if (producto && producto.id) {
      // Modo edición
      setEditingProduct(producto);
      setFormData({
        nombre: producto.nombre,
        descripcion: producto.descripcion || '',
        categoria: producto.categoria.toString(),
        precio_venta: producto.precio_venta.toString(),
        stock_actual: producto.stock_actual.toString(), // Mostrar stock actual para editar
        codigo: producto.codigo || '',
        imagen: producto.imagen || '',
        activo: producto.activo,
      });
      setImagePreview(producto.imagen || '');
    } else {
      // Modo creación
      setEditingProduct(null);
      setFormData({
        nombre: '',
        descripcion: '',
        categoria: '',
        precio_venta: '',
        stock_actual: '',
        codigo: '',
        imagen: '',
        activo: true,
      });
      setImagePreview('');
    }
    setSelectedImage(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setSelectedImage(null);
    setImagePreview('');
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        setSnackbar({ open: true, message: 'Por favor selecciona un archivo de imagen válido', severity: 'error' });
        return;
      }
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ open: true, message: 'La imagen no debe superar los 5MB', severity: 'error' });
        return;
      }
      
      setSelectedImage(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      // Validaciones básicas
      if (!formData.nombre.trim()) {
        setSnackbar({ open: true, message: 'El nombre es requerido', severity: 'error' });
        return;
      }
      if (!formData.categoria) {
        setSnackbar({ open: true, message: 'La categoría es requerida', severity: 'error' });
        return;
      }
      if (!formData.precio_venta || parseFloat(formData.precio_venta) <= 0) {
        setSnackbar({ open: true, message: 'El precio debe ser mayor a 0', severity: 'error' });
        return;
      }
      // Solo validar stock requerido para productos nuevos
      if (!editingProduct && (!formData.stock_actual || parseInt(formData.stock_actual) < 0)) {
        setSnackbar({ open: true, message: 'El stock inicial es requerido para nuevos productos', severity: 'error' });
        return;
      }
      // Para productos existentes, validar que no sea negativo si se ingresó algo
      if (editingProduct && formData.stock_actual && parseInt(formData.stock_actual) < 0) {
        setSnackbar({ open: true, message: 'El stock a agregar no puede ser negativo', severity: 'error' });
        return;
      }

      const productoData: any = {
        codigo: formData.codigo.trim() || 'AUTO',
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || '',
        categoria: parseInt(formData.categoria),
        precio_compra: parseFloat(formData.precio_venta) * 0.7, // 30% de margen
        precio_venta: parseFloat(formData.precio_venta),
        unidad_medida: 'und', // Minúsculas como espera el backend
        stock_minimo: 5,
        activo: formData.activo,
      };
      
      // Manejar stock según si es nuevo o existente
      if (editingProduct && editingProduct.id) {
        // Producto existente: calcular diferencia de stock
        const nuevoStock = parseInt(formData.stock_actual) || 0;
        const stockActual = editingProduct.stock_actual || 0;
        const diferencia = nuevoStock - stockActual;
        
        if (diferencia !== 0) {
          // Solo enviar si hay cambio en el stock
          productoData.ajustar_stock = diferencia; // Puede ser positivo o negativo
        }
      } else {
        // Producto nuevo: stock inicial
        if (formData.stock_actual && parseInt(formData.stock_actual) > 0) {
          productoData.stock_inicial = parseInt(formData.stock_actual);
        }
      }

      // Si hay una nueva imagen seleccionada, agregarla
      if (selectedImage) {
        productoData.imagen = selectedImage;
        console.log('✅ Enviando imagen:', selectedImage.name, selectedImage.type, selectedImage.size);
      } else if (formData.imagen && !editingProduct) {
        // Solo para productos nuevos con URL pero sin archivo
        productoData.imagen = formData.imagen;
      }
      // Para actualización sin nueva imagen, no enviar el campo imagen
      
      console.log('📦 Datos a enviar:', Object.keys(productoData));

      if (editingProduct && editingProduct.id) {
        // Actualizar producto existente
        await productoService.updateProducto(editingProduct.id, productoData);
        setSnackbar({ open: true, message: '✅ Producto actualizado exitosamente', severity: 'success' });
      } else {
        // Crear nuevo producto
        await productoService.createProducto(productoData);
        setSnackbar({ open: true, message: '✅ Producto creado exitosamente', severity: 'success' });
      }

      handleCloseDialog();
      loadProductos(); // Recargar la lista
    } catch (error) {
      console.error('Error al guardar producto:', error);
      setSnackbar({ open: true, message: '❌ Error al guardar el producto', severity: 'error' });
    }
  };

  const handleDelete = async (productoId: number) => {
    if (!window.confirm('¿Está seguro de eliminar este producto?')) {
      return;
    }

    try {
      await productoService.deleteProducto(productoId);
      setSnackbar({ open: true, message: '✅ Producto eliminado', severity: 'success' });
      loadProductos();
    } catch (error) {
      console.error('Error al eliminar:', error);
      setSnackbar({ open: true, message: '❌ Error al eliminar el producto', severity: 'error' });
    }
  };

  const handleToggleActivo = async (producto: Producto) => {
    if (!producto.id) return;
    
    try {
      await productoService.toggleStatus(producto.id);
      setSnackbar({ 
        open: true, 
        message: `✅ Producto ${!producto.activo ? 'activado' : 'desactivado'}`, 
        severity: 'success' 
      });
      loadProductos();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      setSnackbar({ open: true, message: '❌ Error al cambiar el estado', severity: 'error' });
    }
  };

  const getStockStatus = (producto: Producto) => {
    if (producto.stock_actual === 0) {
      return { label: 'Sin Stock', color: 'error' as const };
    } else if (producto.necesita_reposicion || producto.stock_actual <= producto.stock_minimo) {
      return { label: 'Stock Bajo', color: 'warning' as const };
    } else {
      return { label: 'Stock OK', color: 'success' as const };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando productos...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestión de Productos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="large"
        >
          Agregar Producto
        </Button>
      </Box>

      {/* Estadísticas simples */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Productos
            </Typography>
            <Typography variant="h4">
              {productos.length}
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Productos Activos
            </Typography>
            <Typography variant="h4">
              {Array.isArray(productos) ? productos.filter(p => p.activo).length : 0}
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Stock Bajo
            </Typography>
            <Typography variant="h4">
              {Array.isArray(productos) ? productos.filter(p => p.stock_actual <= p.stock_minimo && p.stock_actual > 0).length : 0}
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Sin Stock
            </Typography>
            <Typography variant="h4">
              {Array.isArray(productos) ? productos.filter(p => p.stock_actual === 0).length : 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Botón temporal para inicializar datos - SOLO SI NO HAY PRODUCTOS */}
      {productos.length === 0 && (
        <Box mb={3}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              📦 No hay productos en la base de datos
            </Typography>
            <Typography variant="body2">
              Puedes crear productos manualmente con el botón "Agregar Producto".
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            placeholder="Buscar productos..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
            sx={{ width: 300 }}
          />
        </CardContent>
      </Card>

      {/* Mensajes */}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla/Acordeón de productos */}
      {isMobile ? (
        /* Vista Móvil - Acordeón */
        <Box>
          {Array.isArray(paginatedProductos) && paginatedProductos.map((producto) => {
            const stockStatus = getStockStatus(producto);
            return (
              <StyledAccordion
                key={producto.id}
                expanded={expandedId === producto.id}
                onChange={(_, isExpanded) => setExpandedId(isExpanded ? (producto.id || null) : null)}
              >
                <AccordionHeader expandIcon={<ExpandMore sx={{ color: '#2e5a33' }} />}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                      <Typography fontWeight={600} fontSize="0.95rem">{producto.nombre}</Typography>
                      <Chip
                        label={stockStatus.label}
                        color={stockStatus.color}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: '22px' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                      {producto.codigo} • {formatCurrency(producto.precio_venta)}
                    </Typography>
                  </Box>
                </AccordionHeader>
                <AccordionBody>
                  <Divider sx={{ mb: 2, borderColor: 'rgba(46, 90, 51, 0.15)' }} />
                  
                  <DetailRow>
                    <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Código</Typography>
                    <Typography textAlign="right">{producto.codigo || 'Sin código'}</Typography>
                  </DetailRow>

                  <DetailRow>
                    <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Precio</Typography>
                    <Typography textAlign="right" fontWeight={600} color="#2e5a33">
                      {formatCurrency(producto.precio_venta)}
                    </Typography>
                  </DetailRow>

                  <DetailRow>
                    <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Stock</Typography>
                    <Box textAlign="right">
                      <Typography fontWeight={600}>{producto.stock_actual} unidades</Typography>
                    </Box>
                  </DetailRow>

                  <DetailRow>
                    <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Categoría</Typography>
                    <Typography textAlign="right">{getCategoriaName(producto.categoria)}</Typography>
                  </DetailRow>

                  <DetailRow>
                    <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Estado</Typography>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Chip
                        label={producto.activo ? 'Activo' : 'Inactivo'}
                        color={producto.activo ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </DetailRow>

                  {producto.descripcion && (
                    <DetailRow>
                      <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Descripción</Typography>
                      <Typography textAlign="right" fontSize="0.85rem" sx={{ maxWidth: '60%' }}>
                        {producto.descripcion}
                      </Typography>
                    </DetailRow>
                  )}

                  {/* Acciones */}
                  <Divider sx={{ my: 2, borderColor: 'rgba(46, 90, 51, 0.15)' }} />
                  <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleOpenDialog(producto)}
                      sx={{
                        backgroundColor: '#2e5a33',
                        '&:hover': { backgroundColor: '#3d7342' },
                      }}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={producto.activo ? <Visibility /> : <CheckCircle />}
                      color={producto.activo ? 'warning' : 'success'}
                      onClick={() => handleToggleActivo(producto)}
                    >
                      {producto.activo ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Delete />}
                      color="error"
                      onClick={() => producto.id && handleDelete(producto.id)}
                    >
                      Eliminar
                    </Button>
                  </Box>
                </AccordionBody>
              </StyledAccordion>
            );
          })}
        </Box>
      ) : (
        /* Vista Desktop - Tabla */
        <Paper sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 800 }}>
              {/* Header de la tabla */}
              <Box 
                display="grid" 
                gridTemplateColumns="2fr 1fr 1fr 1fr 1fr 1.2fr 1fr"
                gap={2}
                p={2}
                sx={{ 
                  backgroundColor: 'rgba(46, 90, 51, 0.05)',
                  borderBottom: '2px solid #e8f5e8',
                }}
              >
                <Typography fontWeight={600} color="#2e5a33">Producto</Typography>
                <Typography fontWeight={600} color="#2e5a33">Código</Typography>
                <Typography fontWeight={600} color="#2e5a33">Precio</Typography>
                <Typography fontWeight={600} color="#2e5a33">Stock</Typography>
                <Typography fontWeight={600} color="#2e5a33">Estado</Typography>
                <Typography fontWeight={600} color="#2e5a33">Categoría</Typography>
                <Typography fontWeight={600} color="#2e5a33" textAlign="right">Acciones</Typography>
              </Box>
              
              {/* Filas de productos */}
              {Array.isArray(paginatedProductos) && paginatedProductos.map((producto) => {
                const stockStatus = getStockStatus(producto);
                return (
                  <Box 
                    key={producto.id}
                    display="grid" 
                    gridTemplateColumns="2fr 1fr 1fr 1fr 1fr 1.2fr 1fr"
                    gap={2}
                    p={2}
                    alignItems="center"
                    sx={{ 
                      borderBottom: '1px solid #e8f5e8',
                      '&:hover': { backgroundColor: 'rgba(46, 90, 51, 0.02)' },
                    }}
                  >
                    <Box>
                      <Typography fontWeight={600}>{producto.nombre}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {producto.descripcion}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {producto.codigo}
                    </Typography>
                    <Typography fontWeight={600}>
                      {formatCurrency(producto.precio_venta)}
                    </Typography>
                    <Box>
                      <Typography fontWeight={600}>{producto.stock_actual}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Unidades
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Chip
                        label={stockStatus.label}
                        color={stockStatus.color}
                        size="small"
                      />
                      <Chip
                        label={producto.activo ? 'Activo' : 'Inactivo'}
                        color={producto.activo ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2">
                      {getCategoriaName(producto.categoria)}
                    </Typography>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(producto)}
                        title="Editar producto"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color={producto.activo ? 'warning' : 'success'}
                        onClick={() => handleToggleActivo(producto)}
                        title={producto.activo ? 'Desactivar' : 'Activar'}
                      >
                        {producto.activo ? <Visibility fontSize="small" /> : <CheckCircle fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => producto.id && handleDelete(producto.id)}
                        title="Eliminar producto"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Paginación */}
      {filteredProductos.length > ITEMS_PER_PAGE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredProductos.length)} de {filteredProductos.length} productos
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontWeight: 500,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                  color: 'white',
                },
              },
            }}
          />
        </Box>
      )}

      {filteredProductos.length === 0 && (
        <Box p={3} textAlign="center">
          <Typography variant="h6" color="textSecondary">
            No se encontraron productos
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {searchTerm ? `No hay productos que coincidan con "${searchTerm}"` : 'No hay productos disponibles'}
          </Typography>
        </Box>
      )}

      {/* Diálogo de Crear/Editar Producto */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5">
              {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Nombre del Producto *"
                value={formData.nombre}
                onChange={(e) => handleFormChange('nombre', e.target.value)}
                placeholder="Ej: Arroz Grado 2 1kg"
                sx={{ flex: 2 }}
              />
              <TextField
                fullWidth
                label="Código SKU"
                value={formData.codigo}
                onChange={(e) => handleFormChange('codigo', e.target.value)}
                placeholder="Ej: ARR001"
                sx={{ flex: 1 }}
              />
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descripción"
              value={formData.descripcion}
              onChange={(e) => handleFormChange('descripcion', e.target.value)}
              placeholder="Descripción detallada del producto..."
            />
            
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Categoría *</InputLabel>
                <Select
                  value={formData.categoria}
                  onChange={(e) => handleFormChange('categoria', e.target.value)}
                  label="Categoría *"
                >
                  {Array.isArray(categorias) && categorias.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.activo}
                    onChange={(e) => handleFormChange('activo', e.target.checked)}
                    color="success"
                  />
                }
                label="Producto Activo"
                sx={{ minWidth: '200px' }}
              />
            </Box>
            
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                type="number"
                label="Precio Venta *"
                value={formData.precio_venta}
                onChange={(e) => handleFormChange('precio_venta', e.target.value)}
                placeholder="0"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
              <TextField
                fullWidth
                type="number"
                label={editingProduct ? "Stock Actual" : "Stock Inicial *"}
                value={formData.stock_actual}
                onChange={(e) => handleFormChange('stock_actual', e.target.value)}
                placeholder="0"
                inputProps={{ min: 0 }}
                helperText={editingProduct 
                  ? "Modifica el valor para ajustar el stock" 
                  : "Stock inicial del producto"}
              />
            </Box>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                Imagen del Producto
              </Typography>
              <Box display="flex" gap={2} alignItems="flex-start">
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ minWidth: '200px' }}
                >
                  {selectedImage ? 'Cambiar Imagen' : 'Subir Imagen'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageSelect}
                  />
                </Button>
                
                {imagePreview && (
                  <Box 
                    sx={{ 
                      width: 150, 
                      height: 150, 
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover' 
                      }} 
                    />
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                Formatos aceptados: JPG, PNG, GIF. Tamaño máximo: 5MB
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CloseIcon />}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={<SaveIcon />}
            color="success"
            disabled={uploadingImage}
          >
            {uploadingImage ? 'Subiendo imagen...' : (editingProduct ? 'Actualizar' : 'Crear Producto')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardProductosBasic;